/* eslint-disable @typescript-eslint/no-floating-promises */
/* eslint-disable @typescript-eslint/no-explicit-any */
import axios from 'axios';
import fileDownload from 'js-file-download';
import { call, put, takeEvery, takeLatest } from 'redux-saga/effects';
import { api } from 'services/api';
import { notificationContainer } from 'utils/notificationContainer';
import { PayloadAction } from '@reduxjs/toolkit';
import {
	TReferralsPartnersRequest,
	TReferralsPartnersResponse,
	TPartnersRequest,
	TPartnersResponse,
	TCreateReferralPartnersRequest,
	TAvailableEmsResponse,
	TCreatePartnerRequest,
	TUpdatePartnerRequest,
	TPartnerReportsRequest,
	TPartnerReportsResponse,
	TDownloadPartnerReportRequest,
	TDownloadedPartnerReportFile,
} from './types';
import {
	getReferralsPartnersSuccess,
	assetsInitState,
	getReferralsPartnersRequest,
	getPartnersRequest,
	getPartnersSuccess,
	createReferralPartnersRequest,
	createReferralPartnersSuccess,
	getAvailableEmsSuccess,
	getAvailableEmsRequest,
	createPartnerRequest,
	createPartnerSuccess,
	updatePartnerSuccess,
	updatePartnerRequest,
	getPartnerReportsRequest,
	getPartnerReportsSuccess,
	getPartnerReportsFailure,
	downloadPartnerReportRequest,
	downloadPartnerReportFinished,
} from './reducer';

export function* getReferralsPartnersRequestWorker(
	action: PayloadAction<TReferralsPartnersRequest>,
) {
	const { payload } = action;
	try {
		const response: TReferralsPartnersResponse = yield call(
			api.referrals.getReferralsPartners,
			payload,
		);
		yield put(getReferralsPartnersSuccess(response));
	} catch (error) {
		if (axios.isAxiosError(error)) {
			notificationContainer(String(error), 'error');
		}
		yield put(assetsInitState());
	}
}

export function* getPartnersRequestWorker(action: PayloadAction<TPartnersRequest>) {
	const { payload } = action;
	try {
		const response: TPartnersResponse = yield call(api.referrals.getPartners, payload);
		yield put(getPartnersSuccess(response));
	} catch (error) {
		if (axios.isAxiosError(error)) {
			notificationContainer(String(error), 'error');
		}
		yield put(assetsInitState());
	}
}

export function* createReferralPartnersRequestWorker(
	action: PayloadAction<TCreateReferralPartnersRequest>,
) {
	const { payload } = action;
	try {
		yield call(api.referrals.createReferralPartners, payload.body);

		yield put(createReferralPartnersSuccess());
		yield put(getReferralsPartnersRequest(payload.referralPartnersResponse));
	} catch (error) {
		if (axios.isAxiosError(error)) {
			notificationContainer(String(error?.response?.data?.message ?? error ?? 'Error!'), 'error');
		}
		// yield put(assetsInitState());
	}
}

export function* getAvailableEmsRequestWorker() {
	try {
		const response: TAvailableEmsResponse = yield call(api.referrals.getAvailableEms);
		yield put(getAvailableEmsSuccess(response));
	} catch (error) {
		if (axios.isAxiosError(error)) {
			notificationContainer(String(error), 'error');
		}
		yield put(assetsInitState());
	}
}

export function* createPartnerRequestWorker(action: PayloadAction<TCreatePartnerRequest>) {
	const { payload } = action;
	try {
		yield call(api.referrals.createPartner, payload.body);

		yield put(createPartnerSuccess());
		yield put(getPartnersRequest(payload.partnersResponse));
	} catch (error) {
		if (axios.isAxiosError(error)) {
			notificationContainer(String(error?.response?.data?.message ?? error ?? 'Error!'), 'error');
		}
	}
}

export function* updatePartnerRequestWorker(action: PayloadAction<TUpdatePartnerRequest>) {
	const { payload } = action;
	try {
		yield call(api.referrals.updatePartner, payload.body);

		yield put(updatePartnerSuccess());
		yield put(getPartnersRequest(payload.partnersResponse));
	} catch (error) {
		if (axios.isAxiosError(error)) {
			notificationContainer(String(error?.response?.data?.message ?? error ?? 'Error!'), 'error');
		}
	}
}

export function* getPartnerReportsRequestWorker(action: PayloadAction<TPartnerReportsRequest>) {
	const { payload } = action;
	try {
		const response: TPartnerReportsResponse = yield call(api.referrals.getPartnerReports, payload);
		yield put(getPartnerReportsSuccess(response));
	} catch (error) {
		yield put(getPartnerReportsFailure());
		if (axios.isAxiosError(error)) {
			notificationContainer(String(error?.response?.data?.message ?? error ?? 'Error!'), 'error');
		}
	}
}

export function* downloadPartnerReportRequestWorker(
	action: PayloadAction<TDownloadPartnerReportRequest>,
) {
	const { payload } = action;
	try {
		const file: TDownloadedPartnerReportFile = yield call(
			api.referrals.downloadPartnerReport,
			payload,
		);
		if (file.blob) fileDownload(file.blob, file.fileName);
	} catch (error) {
		if (axios.isAxiosError(error)) {
			// The request expects a blob, so an error body arrives as a Blob holding the JSON response.
			const data = error.response?.data;
			let message: string | undefined =
				typeof data?.message === 'string' ? data.message : undefined;
			if (message === undefined && data instanceof Blob) {
				try {
					const text: string = yield call([data, 'text']);
					const parsed = text ? JSON.parse(text) : {};
					message = parsed?.message ?? parsed?.error ?? (text || 'Download failed');
				} catch {
					message = 'Download failed';
				}
			}
			notificationContainer(String(message ?? error.message ?? 'Download failed'), 'error');
		}
	} finally {
		yield put(downloadPartnerReportFinished(payload.id));
	}
}

export function* referralsSaga() {
	yield takeEvery(getReferralsPartnersRequest.type, getReferralsPartnersRequestWorker);
	yield takeEvery(createReferralPartnersRequest.type, createReferralPartnersRequestWorker);
	yield takeEvery(getPartnersRequest.type, getPartnersRequestWorker);
	yield takeEvery(getAvailableEmsRequest.type, getAvailableEmsRequestWorker);
	yield takeEvery(createPartnerRequest.type, createPartnerRequestWorker);
	yield takeEvery(updatePartnerRequest.type, updatePartnerRequestWorker);
	yield takeLatest(getPartnerReportsRequest.type, getPartnerReportsRequestWorker);
	yield takeEvery(downloadPartnerReportRequest.type, downloadPartnerReportRequestWorker);
}
